import { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Image, Button, Form, Stack } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import useAxios from "axios-hooks";
import BrandContext from "./BrandContext";
import { Company } from "./Company";

const SEARCH_URI = 'https://autocomplete.clearbit.com/v1/companies/suggest?query=';

export default function Details(props) {
    const nav = useNavigate();
    const params = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [formState, setFormState] = useState(null)
    const [sameContact, setSameContact] = useState(false);
    const [valid, setValid] = useState(false);
    const [prContactForm, setPrContactForm] = useState(null);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [prContactFormDisabled, setPrContactFormDisabled] = useState(false);
    const customerNameInput = useRef(null);
    const [showCompanyModal, setShowCompanyModal] = useState(false);

    const [{ data: currentPR, loading: loadingPR, error: prError }, refetchPR] = useAxios({
        method: 'get',
        url: '/pr/get',
        params: {
            id: params.prId
        },
    });

    useEffect(() => {
        const status = currentPR?.status;
        switch (status) {
            case 'paid':
                nav('../checkout');
                return;
            case 'submitted':
                nav('../submitted');
                return;
            default:
                break;
        }
        setValid(isValid(currentPR, false))
    }, [currentPR]);

    const [
        { data: draft, loading: working, error: createError },
        executePut
    ] = useAxios(
        {
            url: '/pr/details',
            method: 'POST'
        },
        { manual: true }
    )

    function updateDetails() {
        const currentFormState = formState || currentPR;
        executePut({
            data: {
                id: params.prId,
                company: currentFormState.company,
                customer: {
                    name: currentFormState.customer.name,
                    email: currentFormState.customer.email,
                    phone: currentFormState.customer.phone
                },
                prContact: {
                    name: sameContact ? currentFormState.customer.name : currentFormState.prContact.name,
                    email: sameContact ? currentFormState.customer.email : currentFormState.prContact.email,
                    phone: sameContact ? currentFormState.customer.phone : currentFormState.prContact.phone
                }
            }
        })
            .then((r) => {
                nav('../content')
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {

            })
    }

    function isValid(newState, sameContact) {
        return !!(newState &&
            newState.company &&
            newState?.customer?.email &&
            newState?.customer?.phone &&
            newState?.customer?.name &&
            (
                sameContact || (
                    newState?.prContact?.email &&
                    newState?.prContact?.name &&
                    newState?.prContact?.phone
                )
            ));
    }

    function setInput(key, value) {
        const currentFormState = formState || currentPR || {};
        let newState, customer, prContact;
        switch (key) {
            case 'company':
                newState = { ...currentFormState, company: value };
                break;
            case 'customerEmail':
                customer = { ...(currentFormState?.customer || {}), email: value }
                newState = { ...currentFormState, customer: customer };
                break;
            case 'customerName':
                customer = { ...(currentFormState?.customer || {}), name: value }
                newState = { ...currentFormState, customer: customer };
                break;
            case 'customerPhone':
                customer = { ...(currentFormState?.customer || {}), phone: value }
                newState = { ...currentFormState, customer: customer };
                break;
            case 'prContactEmail':
                prContact = { ...(currentFormState?.prContact || {}), email: value }
                newState = { ...currentFormState, prContact: prContact };
                break;
            case 'prContactName':
                prContact = { ...(currentFormState?.prContact || {}), name: value }
                newState = { ...currentFormState, prContact: prContact };
                break;
            case 'prContactPhone':
                prContact = { ...(currentFormState?.prContact || {}), phone: value }
                newState = { ...currentFormState, prContact: prContact };
                break;
            default:
                newState = { ...currentFormState }
                break;
        }
        setFormState(newState);
        setValid(isValid(newState, sameContact));
    }

    const handleSearch = (query) => {
        setIsLoading(true);

        fetch(`${SEARCH_URI}${query}`)
            .then((resp) => resp.json())
            .then((items) => {
                const options = items.map((i) => ({
                    logo: i.logo,
                    id: i.domain,
                    domain: i.domain,
                    name: i.name,
                }));

                setOptions(options);
                setIsLoading(false);
            });
    };

    const handleCompanySelect = (event) => {
        setInput('company', event[0])
        setSelectedCompanies([])
        customerNameInput.current.scrollIntoView(true)
        customerNameInput.current.focus()
    }

    const updateSameContact = function (checked) {
        if (checked) {
            prContactForm.reset();
            setSameContact(true)
            setValid(isValid(formState || currentPR || {}, true))
            setPrContactFormDisabled(true);
        } else {
            prContactForm.reset();
            setSameContact(false)
            setValid(isValid(formState || currentPR || {}, false))
            setPrContactFormDisabled(false);
        }
    }

    // Bypass client-side filtering by returning `true`. Results are already
    // filtered by the search endpoint, so no need to do it again.
    const filterBy = () => true;

    return (
        <BrandContext.Consumer>
            {theme =>
                <Fragment>
                    <Container fluid="md" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                        <Row>
                            <Col sm={{ span: true, order: 'first' }} xs={{ span: 12, order: 'last' }} style={{ padding: "15px" }}>
                                <p style={{ fontSize: "14px" }}><span className="fw-bold">Create</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="sr-light">Promote</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="sr-light">Submit</span></p>
                                <p className="fw-bold" style={{ fontSize: "24px", lineHeight: "26px" }}>Which company or organization is the focus of this press release?</p>
                            </Col>
                            <Col sm={{ span: 'auto', order: 'last' }} xs={{ span: 12, order: 'first' }} style={{ padding: "15px", display: "grid", alignContent: "start", justifyContent: "start" }}>
                                <Image style={{ height: "50px" }} fluid src={theme?.marketing?.logo}></Image>
                            </Col>
                        </Row>
                        <Row className="swiftrev-alert" style={{ margin: "12px 0 0 0" }}>
                            <Col style={{ padding: "35px" }}>
                                <AsyncTypeahead
                                    autoFocus
                                    selected={selectedCompanies}
                                    filterBy={filterBy}
                                    id="async-example"
                                    isLoading={isLoading}
                                    labelKey="name"
                                    minLength={3}
                                    onSearch={handleSearch}
                                    options={options}
                                    placeholder="Search for a company by name or domain..."
                                    onChange={handleCompanySelect}
                                    renderMenuItemChildren={(option, props) => (
                                        <Fragment>
                                            <img
                                                alt={option.name}
                                                src={option.logo}
                                                style={{
                                                    height: '24px',
                                                    marginRight: '10px',
                                                    width: '24px',
                                                }}
                                            />
                                            <span>{option.name} - {option.domain}</span>
                                        </Fragment>
                                    )}
                                />
                                <Button onClick={() => setShowCompanyModal({})} style={{ color: "white", padding: "15px 0 0 0", fontSize: "12px" }} variant="link">Can't find your company?</Button>
                            </Col>
                        </Row>
                        <Row hidden={!formState?.company && !currentPR?.company} style={{ padding: "50px 0 0 0" }}>
                            <Col xs="auto" style={{ display: "flex", flexDirection: "column", justifyContent: "start" }}>
                                <div><Image style={{ maxHeight: "64px", maxWidth: "256px" }} fluid src={formState?.company?.logo || currentPR?.company?.logo}></Image></div>
                            </Col>
                            <Col xs="auto" style={{ display: "flex", flexDirection: "column", justifyContent: "start" }}>
                                <h4>{formState?.company?.name || currentPR?.company?.name}</h4>
                                <div>{formState?.company?.domain || currentPR?.company?.domain}</div>
                            </Col>
                        </Row>
                        <div hidden={!formState?.company && !currentPR?.company}><Button onClick={() => setShowCompanyModal(formState?.company || currentPR.company)} style={{ color: "black", padding: "15px 0 0 0", fontSize: "12px" }} variant="link">Edit company details</Button></div>
                        <Row style={{ paddingTop: "25px" }}>
                            <Col md={{ span: 6, order: 1 }} style={{ padding: "15px" }} xs={{ span: 12, order: 1 }}>
                                <h4>Your Contact Information</h4>
                                <p>Private. This information will not be included in the press release and will only be used to contact you regarding your purchase.</p>

                            </Col>
                            <Col md={{ span: 6, order: 2 }} style={{ padding: "15px" }} xs={{ span: 12, order: 3 }}>
                                <h4>Press Release Contact Information</h4>
                                <p>Displayed publicly on the press release.</p>
                                <Form.Check noValidate style={{ margin: "0px" }}
                                    type="checkbox"
                                    id="contact-checkbox"
                                    label="Same as your contact information"
                                    onChange={event => updateSameContact(event.target.checked)}
                                />
                            </Col>
                            <Col md={{ span: 6, order: 3 }} style={{ padding: "15px" }} xs={{ span: 12, order: 2 }}>
                                <Form noValidate validated={valid}>
                                    <Form.Group className="mb-3" controlId="customer.email">
                                        <Form.Label>Email Address</Form.Label>
                                        <Form.Control readOnly
                                            type="email"
                                            defaultValue={currentPR?.customer?.email || ''} />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="customer.name">
                                        <Form.Label>First and Last Name</Form.Label>
                                        <Form.Control ref={customerNameInput} required type="text"
                                            onChange={event => setInput('customerName', event.target.value)}
                                            defaultValue={currentPR?.customer?.name || ''} />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="customer.phone">
                                        <Form.Label>Phone Number</Form.Label>
                                        <Form.Control required type="text"
                                            onChange={event => setInput('customerPhone', event.target.value)}
                                            defaultValue={currentPR?.customer?.phone || ''} />
                                    </Form.Group>
                                </Form>
                            </Col>
                            <Col md={{ span: 6, order: 4 }} style={{ padding: "15px" }} xs={{ span: 12, order: 4 }}>
                                <Form hidden={sameContact} noValidate validated={valid} ref={(form) => setPrContactForm(form)}>
                                    <fieldset disabled={prContactFormDisabled}>
                                        <Form.Group className="mb-3" controlId="prContact.email">
                                            <Form.Label>Email Address</Form.Label>
                                            <Form.Control required type="email"
                                                onChange={event => setInput('prContactEmail', event.target.value)}
                                                defaultValue={currentPR?.prContact?.email || ''}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="prContact.name">
                                            <Form.Label>First and Last Name</Form.Label>
                                            <Form.Control required type="text"
                                                onChange={event => setInput('prContactName', event.target.value)}
                                                defaultValue={currentPR?.prContact?.name || ''}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="prContact.phone">
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control required type="text"
                                                onChange={event => setInput('prContactPhone', event.target.value)}
                                                defaultValue={currentPR?.prContact?.phone || ''}
                                            />
                                        </Form.Group>
                                    </fieldset>
                                </Form>
                            </Col>
                        </Row>
                        <Stack direction="horizontal" gap={3}>
                            <Button onClick={() => updateDetails()} disabled={!valid || working} size="lg" variant="swiftrev">Next</Button>
                        </Stack>
                    </Container>
                    {showCompanyModal !== false && <Company company={showCompanyModal} save={(company) => { setInput('company', company); setShowCompanyModal(false); }} cancel={() => setShowCompanyModal(false)} />}
                </Fragment>
            }
        </BrandContext.Consumer>
    );
}